/**
 * Filtering utilities for the Client Workspace.
 *
 * Provides status-based tab filtering and badge count computation.
 * All operations are pure functions that return new arrays (no mutation).
 *
 * - `filterByStatus` — filters projects by one or more statuses (null = all)
 * - `computeStatusCounts` — computes per-status counts with "99+" cap
 *
 * Counts are always computed from the FULL project list, not a filtered subset.
 *
 * @module filtering
 * @see Requirements 2.3, 2.4, 2.5
 */

import type { ClientProjectSummaryDto, ClientProjectStatus } from '../../../../types/client-workspace';

/**
 * Filters projects by status for tab navigation.
 *
 * @param projects - The full list of projects to filter
 * @param statusFilter - Array of statuses to include, or null to return all projects
 * @returns A new array containing only projects matching the filter
 */
export function filterByStatus(
  projects: ClientProjectSummaryDto[],
  statusFilter: ClientProjectStatus[] | null,
): ClientProjectSummaryDto[] {
  // null filter means "All Projects" — return a shallow copy (no mutation)
  if (statusFilter === null) {
    return [...projects];
  }

  return projects.filter((project) => statusFilter.includes(project.status));
}

/**
 * Computes badge counts for each status tab.
 *
 * Returns a record keyed by each `ClientProjectStatus` value plus `'all'`.
 * Values are the exact count (number) when ≤ 99, or the string "99+" when > 99.
 *
 * @param projects - The FULL project list (not a filtered subset)
 * @returns Record mapping each status (and 'all') to a count or "99+"
 */
export function computeStatusCounts(
  projects: ClientProjectSummaryDto[],
): Record<ClientProjectStatus | 'all', number | string> {
  const counts: Record<ClientProjectStatus | 'all', number> = {
    all: projects.length,
    in_production: 0,
    awaiting_review: 0,
    revision_requested: 0,
    approved: 0,
    delivered: 0,
    blocked: 0,
    waiting_on_client: 0,
  };

  for (const project of projects) {
    if (project.status in counts) {
      counts[project.status]++;
    }
  }

  // Cap values > 99 at "99+"
  const result: Record<ClientProjectStatus | 'all', number | string> = {} as Record<
    ClientProjectStatus | 'all',
    number | string
  >;

  for (const [key, value] of Object.entries(counts)) {
    result[key as ClientProjectStatus | 'all'] = value > 99 ? '99+' : value;
  }

  return result;
}
